import { gql, GraphQLClient } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";

// ========== GraphQL Mutations ==========

const TOGGLE_BOOKMARK_MUTATION = gql`
  mutation ToggleBookmark($lessonId: String!) {
    toggleBookmark(lessonId: $lessonId) {
      success
      isBookmarked
    }
  }
`;

const SAVE_NOTE_MUTATION = gql`
  mutation SaveNote($lessonId: String!, $content: String!) {
    saveNote(lessonId: $lessonId, content: $content) {
      success
      note {
        id
        content
        updatedAt
      }
    }
  }
`;

const DELETE_NOTE_MUTATION = gql`
  mutation DeleteNote($lessonId: String!) {
    deleteNote(lessonId: $lessonId) {
      success
    }
  }
`;

// ========== GraphQL Queries ==========

const GET_BOOKMARK_STATUS_QUERY = gql`
  query GetBookmarkStatus($lessonId: String!) {
    getBookmarkStatus(lessonId: $lessonId) {
      isBookmarked
    }
  }
`;

const GET_NOTE_QUERY = gql`
  query GetNote($lessonId: String!) {
    getNote(lessonId: $lessonId) {
      id
      content
      updatedAt
    }
  }
`;

const GET_USER_BOOKMARKS_QUERY = gql`
  query GetUserBookmarks {
    getUserBookmarks {
      lessonId
      lessonTitle
      lessonSlug
      courseTitle
      courseSlug
      bookmarkedAt
    }
  }
`;

const GET_USER_NOTES_QUERY = gql`
  query GetUserNotes {
    getUserNotes {
      noteId
      lessonId
      lessonTitle
      lessonSlug
      courseTitle
      courseSlug
      content
      updatedAt
    }
  }
`;

// ========== Types ==========

export interface ToggleBookmarkResponse {
  success: boolean;
  isBookmarked: boolean;
}

export interface LessonNote {
  id: string;
  content: string;
  updatedAt: string;
}

export interface SaveNoteResponse {
  success: boolean;
  note: LessonNote;
}

export interface DeleteNoteResponse {
  success: boolean;
}

export interface BookmarkStatus {
  isBookmarked: boolean;
}

export interface UserBookmark {
  lessonId: string;
  lessonTitle: string;
  lessonSlug: string;
  courseTitle: string;
  courseSlug: string;
  bookmarkedAt: string;
}

export interface UserNote {
  noteId: string;
  lessonId: string;
  lessonTitle: string;
  lessonSlug: string;
  courseTitle: string;
  courseSlug: string;
  content: string;
  updatedAt: string;
}

export interface GraphqlLessonEngagementOptions extends GraphqlRequestConfig {
  client?: GraphQLClient;
}

function ensureClient(options?: GraphqlLessonEngagementOptions) {
  if (options?.client) {
    return options.client;
  }
  return createGraphqlClient(options);
}

// ========== API Functions ==========

/**
 * ブックマークをトグル（追加/削除）
 */
export async function toggleBookmark(
  lessonId: string,
  options?: GraphqlLessonEngagementOptions
): Promise<ToggleBookmarkResponse> {
  const client = ensureClient(options);
  const data = await client.request(TOGGLE_BOOKMARK_MUTATION, { lessonId });
  return (data as any).toggleBookmark;
}

/**
 * ブックマーク状態を取得
 */
export async function getBookmarkStatus(
  lessonId: string,
  options?: GraphqlLessonEngagementOptions
): Promise<BookmarkStatus> {
  const client = ensureClient(options);
  const data = await client.request(GET_BOOKMARK_STATUS_QUERY, { lessonId });
  return (data as any).getBookmarkStatus;
}

/**
 * メモを保存
 */
export async function saveNote(
  lessonId: string,
  content: string,
  options?: GraphqlLessonEngagementOptions
): Promise<SaveNoteResponse> {
  const client = ensureClient(options);
  const data = await client.request(SAVE_NOTE_MUTATION, { lessonId, content });
  return (data as any).saveNote;
}

/**
 * メモを取得
 */
export async function getNote(
  lessonId: string,
  options?: GraphqlLessonEngagementOptions
): Promise<LessonNote | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_NOTE_QUERY, { lessonId });
  return (data as any).getNote;
}

/**
 * メモを削除
 */
export async function deleteNote(
  lessonId: string,
  options?: GraphqlLessonEngagementOptions
): Promise<DeleteNoteResponse> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_NOTE_MUTATION, { lessonId });
  return (data as any).deleteNote;
}

/**
 * ユーザーのブックマーク一覧を取得
 */
export async function getUserBookmarks(
  options?: GraphqlLessonEngagementOptions
): Promise<UserBookmark[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_USER_BOOKMARKS_QUERY);
  return (data as any).getUserBookmarks ?? [];
}

/**
 * ユーザーのメモ一覧を取得
 */
export async function getUserNotes(
  options?: GraphqlLessonEngagementOptions
): Promise<UserNote[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_USER_NOTES_QUERY);
  return (data as any).getUserNotes ?? [];
}
